import '../css/tailwind.scss'
import '../css/app.css'
import {createApp, h} from 'vue'
import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import {createInertiaApp} from '@inertiajs/vue3'
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
import "dayjs/locale/de";
import relativeTime from "dayjs/plugin/relativeTime";
import objectSupport from "dayjs/plugin/objectSupport";
import dayjs from "dayjs";
import {createPinia} from 'pinia'
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import {date, decimal, t} from "./helpers";
import 'primeicons/primeicons.css'
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import * as Sentry from "@sentry/vue";

const scheduleIdle = (callback) => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(callback, {timeout: 3000});
        return;
    }

    window.setTimeout(callback, 3000);
};

const initializeConsentManager = async () => {
    try {
        const [{default: klaroConfig}, klaroModule] = await Promise.all([
            import('../../klaro.config.js'),
            import('klaro'),
            import('klaro/dist/klaro.css'),
        ]);

        klaroModule.setup(klaroConfig);
        window.klaro = klaroModule;
    } catch (error) {
        console.error('Failed to initialize consent manager', error);
    }
};

const registerDeferredConsentManager = () => {
    if (typeof window === 'undefined') {
        return;
    }

    let hasInitialized = false;
    const interactionEvents = ['pointerdown', 'keydown', 'touchstart'];

    const initialize = () => {
        if (hasInitialized) {
            return;
        }

        hasInitialized = true;
        interactionEvents.forEach((eventName) => {
            window.removeEventListener(eventName, initialize);
        });

        void initializeConsentManager();
    };

    interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, initialize, {once: true, passive: true});
    });

    window.setTimeout(() => {
        scheduleIdle(initialize);
    }, 10000);
};

if (typeof window !== 'undefined') {
    registerDeferredConsentManager();
}

import axios from 'axios';
import {Tooltip} from "primevue";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE;
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE;

function sanitizeUrl(url) {
    if (typeof url !== "string" || url.length === 0) {
        return url;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);

        return `${parsedUrl.origin}${parsedUrl.pathname}`;
    } catch (error) {
        return url.split("?")[0];
    }
}

function sanitizeSentryEvent(event) {
    event.user = undefined;

    if (event.request) {
        event.request.data = undefined;
        event.request.headers = undefined;
        event.request.cookies = undefined;
        event.request.query_string = undefined;
        event.request.url = sanitizeUrl(event.request.url);
    }

    if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
            if (!breadcrumb || !breadcrumb.data || typeof breadcrumb.data !== "object") {
                return breadcrumb;
            }

            const sanitizedData = { ...breadcrumb.data };
            delete sanitizedData.url;
            delete sanitizedData.data;
            delete sanitizedData.body;
            delete sanitizedData.input;
            delete sanitizedData.headers;
            delete sanitizedData.response;

            return {
                ...breadcrumb,
                data: sanitizedData,
            };
        });
    }

    return event;
}

window.axios = axios;

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * Next we will register the CSRF Token as a common header with Axios so that
 * all outgoing HTTP requests automatically have it attached. This is just
 * a simple convenience so we don't have to attach every token manually.
 */

let token = document.head.querySelector('meta[name="csrf-token"]');

if (token) {
    window.axios.defaults.headers.common["X-CSRF-TOKEN"] = token.content;
} else {
    console.error(
        "CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token"
    );
}

[
    duration,
    utc,
    isoWeek,
    weekOfYear,
    isSameOrBefore,
    isBetween,
    relativeTime,
    objectSupport,
].forEach((plugin) => dayjs.extend(plugin));
dayjs.locale("de");


const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel';

createInertiaApp({
    progress: {
        color: '#29d',
    },
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    title: (title) => `${title} - urlaubsleute.de`,
    setup({el, App, props, plugin}) {
        const app = createApp({render: () => h(App, props)})
            .use(plugin)
            .directive('tooltip', Tooltip)
            .use(createPinia())
            .mixin({
                methods: {
                    dayjs,
                    decimal,
                    date,
                    datetime: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
                    t: t,
                }
            })
            .use(PrimeVue, {
                theme: {
                    preset: Aura,
                    options: {
                        darkModeSelector: '.my-app-dark',
                    }
                },
            })
            .use(ConfirmationService)
            .use(ToastService);


        if (sentryDsn) {
            Sentry.init({
                app,
                dsn: sentryDsn,
                environment: sentryEnvironment,
                release: sentryRelease,
                tracesSampleRate: 0,
                sendDefaultPii: false,
                beforeSend: sanitizeSentryEvent,
            });
        }


        app.mount(el)
    },
});

